import * as React from 'react';
import { ITooltipPlacement, ITooltipTrigger } from './Tooltip.types';
import { createLocators } from '@u1/locators';
import { useTooltipOpen, useTooltipPlacement } from './Tooltip.hooks';
import clsx from 'clsx';
import useClasses from './Tooltip.styles';

export interface ITooltipProps {
  /**
   * Tooltip trigger
   */
  children: React.ReactNode;
  /**
   * Tooltip content
   */
  content: React.ReactNode;
  /**
   * Tooltip placement
   * @default top-center
   */
  placement?: ITooltipPlacement;
  /**
   * Tooltip is opened before any interactions
   * @default false
   */
  initiallyOpen?: boolean;
  /**
   * Type of tooltip trigger handlers
   * @default hover
   */
  trigger?: ITooltipTrigger;

  /**
   * Delay before tooltip is shown, ms
   * @default hover
   */
  enterDelay?: number;

  /**
   * Delay before tooltip is hidden, ms
   * @default hover
   */
  leaveDelay?: number;

  /**
   * Limits maximum available width
   */
  maxWidth?: string;
}

export const tooltipLocators = createLocators({
  name: 'Tooltip',
  componentLocators: ['tooltip', 'arrow', 'content'],
});
const { locators } = tooltipLocators();

const Tooltip: React.FC<ITooltipProps> = ({
  children,
  content,
  initiallyOpen = false,
  placement: initialPlacement = 'top-center',
  trigger = 'hover',
  enterDelay = 0,
  leaveDelay = 300,
  maxWidth = 'auto',
}) => {
  const classes = useClasses();
  const { open, triggerEvents, triggerRef, contentEvents } = useTooltipOpen({
    trigger,
    initiallyOpen,
    enterDelay,
    leaveDelay,
  });
  const { ref, placement } = useTooltipPlacement({ initialPlacement, open });

  return (
    <div
      {...locators.tooltip()}
      className={classes.root}
      {...triggerEvents}
      ref={triggerRef}
    >
      {children}
      <div
        {...locators.content({ isOpen: open })}
        style={{ maxWidth: maxWidth }}
        className={clsx(
          classes.content,
          classes[`content-${placement.main}`],
          classes[placement.shift],
          {
            [classes.open]: open,
          },
        )}
        ref={ref}
        {...contentEvents}
      >
        {content}
        <div
          {...locators.arrow({
            placement: placement.main,
            shift: placement.shift,
          })}
          className={clsx(
            classes.arrow,
            classes[`arrow-${placement.main}`],
            classes[placement.shift],
          )}
        />
      </div>
    </div>
  );
};

export default Tooltip;
